import type { MediaAsset } from '@/constants/media'

import { media } from '@/constants/media'

export type Pillar = {
    title: string
    description: string
}

export type SkillGroup = {
    index: string
    title: string
    description: string
    tools: string[]
    href: string
    image: MediaAsset
}

export type NumberHighlight = {
    value: number
    suffix: string
    label: string
    description: string
}

export const heroContent = {
    eyebrow: 'Portofolio',
    lines: ['Iza', 'Faroza', 'Developer'],
    kicker: 'Web Developer & Pembina Pramuka',
    location: 'Bogor / Indonesia',
    description: 'Mahasiswa Ilmu Komputer di STIKOM El Rahma Bogor yang membangun solusi web dan aktif memimpin di organisasi.',
    primaryAction: 'Hubungi saya',
    secondaryAction: 'Download CV',
    captionLabel: 'Satu orang / ',
    caption: 'teknologi dan kepemimpinan, berjalan bersama.',
    scrollLabel: 'Gulir ke bawah',
    badge: 'Gulir untuk melihat / Iza Faroza /'
}

export const heroCards = [
    { image: media.portraitFormal, label: 'Mahasiswa' },
    { image: media.presenting, label: 'Presentasi' },
    { image: media.teaching, label: 'Pembina' },
    { image: media.laptop, label: 'Ngoding' },
    { image: media.hoodie, label: 'Kreatif' },
    { image: media.portraitDark, label: 'STIKOM' }
]

export const manifestoContent = {
    label: 'Tentang Saya',
    statement: [
        { text: 'Menggabungkan' },
        { image: media.laptop },
        { text: 'dunia teknologi' },
        { text: 'dengan keterampilan' },
        { image: media.teaching },
        { text: 'kepemimpinan' },
        { image: media.presenting },
        { text: 'sosial.' }
    ] as { text?: string, image?: MediaAsset }[],
    accentWords: ['teknologi', 'sosial.'],
    description: 'Mahasiswa Ilmu Komputer STIKOM El Rahma Bogor yang saat ini juga bekerja sebagai Admin VIP di J&T Express.'
}

export const pillars: Pillar[] = [
    {
        title: 'Pengembangan Web',
        description: 'Spesialisasi di HTML, CSS, JavaScript, PHP natif dan sistem basis data relasional untuk merekayasa solusi web.'
    },
    {
        title: 'Kepemimpinan & Organisasi',
        description: 'Pembina Pramuka, sekretaris dan logistik di berbagai kepanitiaan. Terbiasa memimpin, mendokumentasikan dan berkoordinasi.'
    },
    {
        title: 'Kreatif & Produktivitas',
        description: 'Desain dengan Canva, penyuntingan video dengan CapCut dan Filmora, serta mahir Microsoft Office.'
    }
]

export const skillsContent = {
    label: 'Keahlian',
    count: '12 Tools',
    kicker: 'Bidang',
    description: 'Kemampuan teknis dan kreatif yang saya gunakan untuk membangun website, sistem informasi dan konten digital.'
}

export const skillGroups: SkillGroup[] = [
    {
        index: '01',
        title: 'Front-End',
        description: 'Membangun tampilan web yang rapi, responsif dan mudah digunakan.',
        tools: ['HTML5', 'CSS3', 'JavaScript'],
        href: '/projects?filter=website',
        image: media.laptop
    },
    {
        index: '02',
        title: 'Back-End & Database',
        description: 'Merancang logika aplikasi dengan PHP natif dan basis data relasional MySQL.',
        tools: ['PHP', 'MySQL', 'GitHub'],
        href: '/projects?filter=sistem-informasi',
        image: media.presenting
    },
    {
        index: '03',
        title: 'Desain & Video',
        description: 'Membuat materi visual dan menyunting video untuk kebutuhan konten dan dokumentasi.',
        tools: ['Canva', 'CapCut', 'Filmora'],
        href: '/certificates',
        image: media.hoodie
    },
    {
        index: '04',
        title: 'Produktivitas Kantor',
        description: 'Menyusun dokumen, laporan, data dan presentasi dengan rapi dan terstruktur.',
        tools: ['Microsoft Word', 'Microsoft Excel', 'Microsoft PowerPoint'],
        href: '/projects?filter=karya-tulis',
        image: media.teaching
    }
]

export const allSkills = skillGroups.flatMap(group => group.tools)

export const selectedWorkContent = {
    label: 'Proyek',
    description: 'Sistem informasi, website dan karya tulis ilmiah yang saya kerjakan selama kuliah di STIKOM El Rahma.',
    kicker: 'Proyek',
    action: 'Lihat semua proyek',
    closing: ['Lihat semua', 'proyek']
}

export const showcaseContent = {
    eyebrow: 'Sertifikat',
    title: 'Terus',
    accent: 'Belajar',
    action: 'Lihat semua sertifikat'
}

export const numbersContent = {
    label: 'Dalam Angka',
    title: 'Dalam',
    subtitle: 'Angka',
    description: 'Sedikit gambaran perjalanan saya di dunia teknologi, organisasi dan pendidikan.',
    imageLabel: 'Pembina Pramuka',
    imageMetric: '50+',
    imageCaption: 'Siswa bimbingan',
    note: 'Membina anggota Pramuka di MI Tarbiyatul Falah, madrasah tempat saya dulu belajar.'
}

export const numbers: NumberHighlight[] = [
    {
        value: 4,
        suffix: '+',
        label: 'Proyek Selesai',
        description: 'Sistem informasi sekolah, PPDB berbasis web, toko online, hingga karya tulis dan jurnal ilmiah.'
    },
    {
        value: 2,
        suffix: '+',
        label: 'Tahun Pengalaman',
        description: 'Aktif di kepanitiaan, kepramukaan dan administrasi, dari sekretaris hingga logistik.'
    },
    {
        value: 50,
        suffix: '+',
        label: 'Siswa Bimbingan',
        description: 'Mendidik anggota Pramuka sekaligus mengelola administrasi dan perlengkapan kegiatannya.'
    }
]

export const experienceContent = {
    label: 'Pengalaman',
    title: 'Pengalaman dan',
    accent: 'organisasi.',
    subtitle: 'Dari sekretaris kepanitiaan hingga Admin VIP di J&T Express.',
    description: 'Setiap peran mengajarkan cara bekerja dalam tim, mengelola administrasi dan bertanggung jawab atas hasil.',
    stagesLabel: 'Peran'
}

export const educationContent = {
    label: 'Pendidikan',
    since: 'Sejak 2012',
    title: 'Riwayat',
    accent: 'Pendidikan',
    statement: 'Dari madrasah, pondok pesantren, hingga kampus Ilmu Komputer.',
    description: 'Saat ini menempuh studi Ilmu Komputer di STIKOM El Rahma Bogor, setelah enam tahun belajar di Pondok Pesantren Daarul Uluum Campus 2.',
    action: 'Selengkapnya tentang saya',
    toolsLabel: 'Tools',
    toolsTitle: 'Teknologi yang',
    toolsAccent: 'saya gunakan.',
    toolsStatement: 'Dari baris kode pertama sampai presentasi akhir, inilah alat yang menemani setiap proyek.',
    toolsDescription: 'Web development, desain, penyuntingan video dan produktivitas kantor.'
}

export const ctaContent = {
    label: 'Kontak',
    eyebrow: 'Kolaborasi',
    title: 'Punya ide proyek? Mari',
    accent: 'berkolaborasi.',
    action: 'Hubungi saya',
    emailLabel: 'Email',
    footerDescription: 'Mahasiswa Ilmu Komputer dan web developer dari Bogor yang menggabungkan teknologi dengan kepemimpinan sosial.'
}

export const aboutContent = {
    eyebrow: 'Tentang Saya',
    kicker: 'Web Developer & Pembina Pramuka',
    description: 'Mahasiswa Ilmu Komputer STIKOM El Rahma Bogor, web developer, dan Pembina Pramuka di MI Tarbiyatul Falah.',
    action: 'Lihat proyek saya',
    portraitCaption: 'Mahasiswa / STIKOM El Rahma',
    whyLabel: 'Prinsip',
    whyBefore: 'Menggabungkan dunia',
    whyAccent: 'teknologi',
    whyAfter: 'dengan keterampilan kepemimpinan sosial.',
    whyDescription: 'Saya percaya teknologi paling berguna ketika dibangun oleh orang yang mengerti cara bekerja bersama manusia.',
    storyLabel: 'Cerita Saya',
    storyHeading: 'Belajar, membangun, dan memimpin.',
    storyAccent: 'membangun,',
    story: [
        'Saya Iza Faroza, mahasiswa program studi Ilmu Komputer di STIKOM El Rahma Bogor. Saya belajar merekayasa solusi web yang inovatif, dan saat ini juga bekerja di J&T Express sebagai Admin VIP.',
        'Saya memiliki spesialisasi dalam arsitektur dasar HTML, CSS, JavaScript, PHP natif dan sistem basis data relasional. Di luar kode, saya mempraktikkan keterampilan kreatif seperti desain Canva, penyuntingan video dengan Filmora dan CapCut, serta produktivitas kantor.',
        'Pengalaman di Pramuka dan berbagai kepanitiaan membentuk cara saya bekerja: teliti dalam administrasi, sigap di lapangan, dan terbiasa memimpin tim.'
    ],
    activityCaption: 'Mengajar / MI Tarbiyatul Falah',
    experienceLabel: 'Pengalaman',
    experienceTitle: 'Organisasi &',
    experienceAccent: 'pekerjaan.',
    experienceDescription: 'Peran yang pernah dan sedang saya jalani, dari kepanitiaan sekolah hingga dunia kerja.',
    educationLabel: 'Pendidikan',
    educationTitle: 'Riwayat',
    educationAccent: 'pendidikan.',
    educationDescription: 'Perjalanan belajar saya dari madrasah hingga perguruan tinggi.',
    skillsLabel: 'Keahlian',
    skillsTitle: 'Alat dan',
    skillsAccent: 'kemampuan.',
    certificatesLabel: 'Sertifikat',
    certificatesTitle: 'Terus',
    certificatesAccent: 'belajar.',
    certificatesDescription: 'Workshop, seminar dan penghargaan yang saya ikuti selama kuliah.',
    certificatesAction: 'Lihat semua sertifikat'
}

export const projectsPageContent = {
    eyebrow: 'Karya Pilihan',
    title: 'Proyek',
    accent: 'Penelitian',
    ideasLabel: 'Punya Ide',
    ideasText: 'Butuh website, sistem informasi, atau rekan kolaborasi? Ceritakan kebutuhan Anda dan mari wujudkan bersama.',
    ideasAction: 'Hubungi saya',
    label: 'Proyek',
    collectionTitle: 'Proyek & Penelitian',
    collectionDescription: 'Sistem informasi, website dan karya tulis ilmiah yang saya kerjakan selama kuliah.',
    kicker: 'Proyek',
    heroCaption: 'Presentasi proyek / STIKOM El Rahma',
    empty: 'Proyek baru sedang disiapkan. Kunjungi lagi nanti.'
}

export const certificatesPageContent = {
    eyebrow: 'Sertifikat & Penghargaan',
    title: 'Sertifikat',
    accent: 'Penghargaan',
    description: 'Workshop, seminar, kepanitiaan dan penghargaan yang saya ikuti selama menempuh studi di STIKOM El Rahma Bogor.',
    kicker: 'Sertifikat',
    allLabel: 'Semua',
    empty: 'Belum ada sertifikat yang ditampilkan.',
    emptyFilter: 'Belum ada sertifikat di kategori ini.',
    open: 'Perbesar',
    close: 'Tutup'
}

export const contactPageContent = {
    eyebrow: 'Kontak',
    lines: ['Mari', 'Berkolaborasi'],
    ideasLabel: 'Punya Ide',
    ideasText: 'Ingin berdiskusi tentang pembuatan website, sistem informasi sekolah, kolaborasi kepramukaan, atau jasa freelance kreatif? Hubungi saya langsung.',
    ideasAction: 'Kirim pesan',
    formLabel: 'Formulir Kontak',
    formTitle: 'Setiap proyek hebat dimulai dari sebuah',
    formAccent: 'obrolan.',
    formSubtitle: 'Ceritakan ide atau kebutuhan Anda.',
    formDescription: 'Pesan Anda langsung masuk ke email saya. Untuk respons lebih cepat, hubungi saya lewat WhatsApp.',
    interests: ['Proyek Website', 'Sistem Informasi', 'Kolaborasi Pendidikan', 'Jasa Freelance', 'Keperluan Lainnya'],
    submit: 'Kirim Pesan',
    replyNote: 'Biasanya dibalas dalam 1x24 jam',
    channelsLabel: 'Hubungi Langsung',
    channelsTitle: 'Hubungi',
    channelsAccent: 'saya langsung.',
    emailLabel: 'Atau kirim email ke',
    channels: [
        { label: 'WhatsApp', description: 'Chat langsung untuk respons tercepat' },
        { label: 'Email', description: 'Kirim pesan kapan saja untuk pertanyaan apa pun' },
        { label: 'Lokasi', description: 'Bogor, Jawa Barat, Indonesia' }
    ]
}
